import {
  createMetadataFromServer,
  createMetadataImageFromServer,
  createResourceRegistryListFromServer,
  getMetadataImageListFromServer,
  getMetadataListFromServer,
  getMetadataTemplateFromServer,
  getResourceRegistryListFromServer,
  updateMetadataFromServer,
  updateMetadataImageFromServer,
} from "logic/services/Metadata-Manager";
import { actionController } from "../utils";

export const metadataManagerController = {
  //metadata등 사용자 입력을 위한 템플릿 조회
  getMetadataTemplate: async (option?: {
    lastId?: number;
    offset?: number;
    limit?: number;
    order?: "ASC" | "DESC";
    categoryIdentifiers?: number[];
    types?: string[];
  }) => {
    return await actionController(async () => {
      try {
        const template = await getMetadataTemplateFromServer({
          params: {},
          query: {
            ...(option?.lastId && { lastId: option.lastId }),
            ...(option?.offset && { offset: option.offset }),
            ...(option?.limit && { limit: option.limit }),
            ...(option?.order && { order: option.order }),
            ...(option?.categoryIdentifiers && {
              categoryIdentifiers: option?.categoryIdentifiers,
            }),
            ...(option?.types && { types: option?.types }),
          },
        });

        // map을 사용하여 각 row에 대해 비동기 작업을 수행
        const rowsWithTemplateData = await Promise.all(
          template.rows.map(async (row) => {
            const s3Url = row.template_uri;

            const response = await fetch(s3Url, { cache: "no-store" });

            if (!response.ok) {
              throw new Error("Failed_to_fetch_JSON_from_S3");
            }

            const jsonData = await response.json();

            // 각 row에 template_data 추가
            return {
              ...row,
              template_data: jsonData,
            };
          })
        );

        // template.rows에 template_data가 추가된 새로운 객체 반환
        return {
          ...template,
          rows: rowsWithTemplateData,
        };
      } catch (error) {
        throw new Error("template_get_failed");
      }
    });
  },

  // 사용자가 저장한 Metadata 목록 조회
  getMetadataList: async (option?: {
    lastId?: number;
    offset?: number;
    limit?: number;
    order?: "ASC" | "DESC";
    resourceRegistryId?: number;
    templateId?: number;
  }) => {
    return await actionController(async () => {
      try {
        const metadata = await getMetadataListFromServer({
          params: {},
          query: {
            ...(option?.lastId && { lastId: option.lastId }),
            ...(option?.offset && { offset: option.offset }),
            ...(option?.limit && { limit: option.limit }),
            ...(option?.order && { order: option.order }),
            ...(option?.resourceRegistryId && {
              resourceRegistryId: option.resourceRegistryId,
            }),
            ...(option?.templateId && { templateId: option.templateId }),
          },
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_get_failed");
      }
    });
  },

  // 사용자 Metadata 생성
  createMetadata: async (
    resourceRegistryId: number,
    templateId: number,
    option?: {
      metadataUri?: string;
      metaData?: any;
    }
  ) => {
    return await actionController(async () => {
      try {
        const formData = new FormData(); // if문 밖에서 선언
        const jsonBlob = new Blob([JSON.stringify(option?.metaData)], {
          type: "application/json",
        });
        const metadataFile = new File([jsonBlob], "metadata.json", {
          type: "application/json",
        });

        formData.append("metadata", metadataFile);
        const metadata = await createMetadataFromServer({
          params: {},
          query: {},
          data: {
            resourceRegistryId,
            templateId,
            ...(option?.metadataUri && { metadataUri: option.metadataUri }),
            ...(formData && { formData: formData }),
          },
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_create_failed");
      }
    });
  },

  // 사용자 Metadata 수정
  updateMetadata: async (
    metadataId: number,
    option?: {
      metadataUri?: string;
    }
  ) => {
    return await actionController(async () => {
      try {
        const metadata = await updateMetadataFromServer({
          params: { metadataId },
          query: {},
          data: {
            ...(option?.metadataUri && { metadataUri: option.metadataUri }),
          },
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_update_failed");
      }
    });
  },

  //이미지 목록 조회
  getMetadataImageList: async (option?: {
    lastId?: number;
    offset?: number;
    limit?: number;
    order?: "ASC" | "DESC";
    resourceRegistryId?: number;
  }) => {
    return await actionController(async () => {
      try {
        const metadata = await getMetadataImageListFromServer({
          params: {
            ...(option?.lastId && { lastId: option.lastId }),
            ...(option?.offset && { offset: option.offset }),
            ...(option?.limit && { limit: option.limit }),
            ...(option?.order && { order: option.order }),

            ...(option?.resourceRegistryId && {
              resourceRegistryId: option.resourceRegistryId,
            }),
          },
          query: {},
          data: {},
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_image_get_failed");
      }
    });
  },

  //이미지 생성
  createMetadataImage: async (
    resourceRegistryId: number,
    option?: {
      formData?: File;
      imageUri?: string;
    }
  ) => {
    return await actionController(async () => {
      const formData = new FormData();
      if (option?.formData) {
        formData.append("image", option?.formData);
      }

      try {
        const metadata = await createMetadataImageFromServer({
          params: {},
          query: {
            resourceRegistryId,
            ...(option?.imageUri && { imageUri: option.imageUri }),
          },
          data: option?.formData ? formData : option?.imageUri, //formData거나 uri둘중에 하나 선택
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_image_create_failed");
      }
    });
  },

  //이미지 수정
  updateMetadataImage: async (
    imageId: number,
    option?: {
      metadataUri?: string;
    }
  ) => {
    return await actionController(async () => {
      try {
        const metadata = await updateMetadataImageFromServer({
          params: { imageId },
          query: {},
          data: {
            ...(option?.metadataUri && {
              metadataUri: option.metadataUri,
            }),
          },
        });
        return metadata;
      } catch (error) {
        throw new Error("metadata_image_update_failed");
      }
    });
  },

  //Resource Registry 목록 조회
  getResourceRegistryList: async (option?: {
    lastId?: number;
    offset?: number;
    limit?: number;
    order?: "ASC" | "DESC";
    categoryIdentifier?: number;
    serviceIdentifier?: string;
    resourceIdentifier?: number;
  }) => {
    return await actionController(async () => {
      try {
        const metadata = await getResourceRegistryListFromServer({
          params: {},
          query: {
            ...(option?.lastId && { lastId: option.lastId }),
            ...(option?.offset && { offset: option.offset }),
            ...(option?.limit && { limit: option.limit }),
            ...(option?.order && { order: option.order }),
            ...(option?.categoryIdentifier && {
              categoryIdentifier: option.categoryIdentifier,
            }),
            ...(option?.serviceIdentifier && {
              serviceIdentifier: option.serviceIdentifier,
            }),
            ...(option?.resourceIdentifier && {
              resourceIdentifier: option.resourceIdentifier,
            }),
          },
          data: {},
        });
        return metadata;
      } catch (error) {
        throw new Error("ResourceRegistry_get_failed");
      }
    });
  },

  //Resource Registry 생성
  createResourceRegistryList: async (option?: {
    categoryIdentifier?: number;
    serviceIdentifier?: string;
    resourceIdentifier?: string;
  }) => {
    return await actionController(async () => {
      try {
        const metadata = await createResourceRegistryListFromServer({
          params: {},
          query: {
            ...(option?.categoryIdentifier && {
              categoryIdentifier: option.categoryIdentifier,
            }),
            ...(option?.serviceIdentifier && {
              serviceIdentifier: option.serviceIdentifier,
            }),
            ...(option?.resourceIdentifier && {
              resourceIdentifier: option.resourceIdentifier,
            }),
          },
          data: {},
        });
        return metadata;
      } catch (error) {
        throw new Error("ResourceRegistry_create_failed");
      }
    });
  },
};
